import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';
import type { GoogleSearchData, GooglePMaxData, MetaData } from '../components/types';

/** Discriminated union of platform-specific deployment data. */
export type PlatformData = GoogleSearchData | GooglePMaxData | MetaData;

export type CampaignObjective = 'awareness' | 'leads' | 'conversions';
/** Channel values used in campaign briefs — which ad channels to target. */
export type CampaignChannel =
  | 'google_search'
  | 'google_pmax'
  | 'google_lsa'
  | 'google_display'
  | 'meta'
  | 'tiktok';
export type CampaignStatus =
  | 'recommended'
  | 'draft'
  | 'generating'
  | 'pending_creative'
  | 'ready_for_review'
  | 'approved'
  | 'launching'
  | 'live'
  | 'dismissed';
export type DeployStatus = 'draft' | 'ready' | 'approved' | 'launching' | 'live' | 'failed';

/** AI-suggested asset wishlist on a campaign brief — image descriptions the
 *  agency should source. Distinct from uploaded MediaAssets. */
export interface BriefAsset {
  url: string;
  type: 'image' | 'video' | 'logo';
  name: string;
}

export interface CampaignBrief {
  objective: CampaignObjective;
  offer: string;
  cta: string;
  targetAudience: string;
  keyMessages: string[];
  geo: string;
  monthlyBudget: number;
  landingUrl?: string;
  platforms: CampaignChannel[];
  assets: BriefAsset[];
}

export type MediaPurpose = 'creative' | 'logo' | 'content_image' | 'avatar' | 'review_qr' | 'other';

export interface MediaAsset {
  id: string;
  locationId: string;
  bucket: string;
  key: string;
  url: string;
  mimeType: string;
  sizeBytes: number;
  originalFilename: string;
  width: number | null;
  height: number | null;
  purpose: MediaPurpose;
  status: 'pending' | 'ready' | 'failed';
  createdAt: string;
}

export interface CampaignAssetAttachment {
  campaignId: string;
  mediaAssetId: string;
  role: string;
  order: number;
  createdAt: string;
  mediaAsset: MediaAsset;
}

export interface Deployment {
  id: string;
  platform: string;
  channel: string | null;
  name: string;
  status: string;
  deployStatus: DeployStatus;
  externalId: string | null;
  platformData: PlatformData | null;
  assets: CampaignAssetAttachment[];
  budget: number | null;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  roas: number;
  launchedAt: string | null;
  errorMessage: string | null;
  /** Set when the campaign was removed directly on the platform (e.g. in Google
   *  Ads). It's kept on record but read-only — nothing can launch/push/edit. */
  removedFromPlatformAt?: string | null;
  /** Field-by-field diff of a staged pending revision vs the live config. */
  pendingDiff?: CampaignDiff | null;
  /** PracticeNova's staged new version (e.g. replaced from a file), not yet on
   *  the platform. Shown on the Setup tab; "Update on Google" pushes it. */
  pendingUpdate?: { platformData?: PlatformData | null; importedAt?: string } | null;
  createdAt: string;
  updatedAt: string;
}

export type ChangeMechanism = 'live_edit' | 'replace' | 'recreate';

export interface CampaignFieldChange {
  field: string;
  label: string;
  mechanism: ChangeMechanism;
  money?: boolean;
  status?: boolean;
  summary: string;
  before?: string | null;
  after?: string | null;
}

export interface CampaignDiff {
  channel: 'google_search' | 'meta';
  changes: CampaignFieldChange[];
  hasChanges: boolean;
  hasMoneyImpact: boolean;
  hasRecreate: boolean;
}

/** Options for pushing a staged pending revision to the platform. */
export interface ApplyPendingOptions {
  /** Deliberate typed confirmation — must equal 'PROCEED'. */
  confirmPhrase?: string;
  confirmMoney?: boolean;
  confirmStatus?: boolean;
  pauseFirst?: boolean;
  overrideDrift?: boolean;
}

/** The phrase the operator must type to push to a live campaign. */
export const APPLY_CONFIRM_PHRASE = 'PROCEED';

/** Result of pushing a pending revision. */
export interface ApplyPendingResult {
  applied: CampaignFieldChange[];
  manual: CampaignFieldChange[];
  failed: { field: string; label: string; error: string }[];
  pausedForEdit: boolean;
  finalStatus: 'ENABLED' | 'PAUSED';
}

export interface MarketingCampaign {
  id: string;
  practiceId: string;
  locationId: string | null;
  name: string;
  source: 'pn-ai' | 'external';
  /** Display order for imported campaigns (file priority, else file position);
   *  null for campaigns created without one. Lower surfaces first. */
  priority: number | null;
  objective: CampaignObjective | null;
  brief: CampaignBrief | null;
  status: CampaignStatus;
  generatedAt: string | null;
  startDate: string | null;
  endDate: string | null;
  createdAt: string;
  updatedAt: string;
  location: {
    id: string;
    name: string;
    website?: string | null;
    practice?: { website?: string | null } | null;
  } | null;
  campaigns: Deployment[];
  /** Set by getCampaign: the practice flag is on AND every deployment is
   *  local-only (never on a platform), so a local Delete is safe to offer. */
  deletable?: boolean;
  /** Set by getCampaign: the force-delete flag is on, the campaign IS on a
   *  platform, and nothing is actively serving — so a nuclear PracticeNova-only
   *  delete (no platform impact) may be offered with a typed confirmation. */
  forceDeletable?: boolean;
}

/* ─── Performance types ─────────────────────────────────────────────────────── */

export interface PerformanceSummary {
  totalSpend: number;
  spendByChannel: Record<string, number>;
  impressions: number;
  impressionsByChannel: Record<string, number>;
  clicks: number;
  ctr: number;
  conversions: number;
  cpa: number;
}

export interface ChannelRow {
  channel: string;
  spend: number;
  impressions: number;
  clicks: number;
  ctr: number;
  conversions: number;
  cpa: number;
}

export interface CampaignRow {
  id: string;
  marketingCampaignId: string | null;
  name: string;
  platform: string;
  status: string;
  spend: number;
  impressions: number;
  clicks: number;
  ctr: number;
  conversions: number;
  cpa: number;
  /** Active campaign launched in this window with no metrics yet — show a
   *  "just launched" row with dashes instead of zeros. */
  awaitingData?: boolean;
}

export interface SearchTermRow {
  term: string;
  impressions: number;
  clicks: number;
  conversions: number;
  cost: number;
}

export interface HeatmapCell {
  dayOfWeek: number;
  hour: number;
  conversions: number;
}

export interface HeatmapData {
  cells: HeatmapCell[];
  aiInsight: string | null;
}

export interface CampaignInsight {
  type:
    | 'spend_waste'
    | 'top_performer'
    | 'cpa_vs_benchmark'
    | 'volume_gap'
    | 'negative_opportunity'
    | 'quality_signal';
  title: string;
  body: string;
  metric?: string;
  sentiment: 'positive' | 'neutral' | 'negative';
}

export interface SearchTermCategory {
  category: string;
  description: string;
  spend: number;
  conversions: number;
  clicks: number;
  term_count: number;
}

export interface CampaignInsightsData {
  insights: CampaignInsight[];
  categories: SearchTermCategory[];
  generatedAt: string;
}

/* ─── Audit + Overview types (Ads v9) ─────────────────────────────────────── */

export type AdsAuditDeductionCategory =
  | 'conversion_tracking'
  | 'budget_efficiency'
  | 'campaign_health'
  | 'ad_quality'
  | 'landing_structure'
  | 'geo_schedule';

export type AdsAuditScorerPlatform = 'google' | 'meta' | 'account';

export interface AdsAuditDeduction {
  category: AdsAuditDeductionCategory;
  rule: string;
  points: number;
  detail: string;
  affectedItems?: string[];
  /** Which platform's scorer produced this. 'account' = cross-platform rule. */
  platform?: AdsAuditScorerPlatform;
}

export interface AdsAuditPlatformBreakdown {
  score: number;
  spend: number;
  breakdown: AdsAuditBreakdown;
}

export interface AdsAuditBreakdown {
  startScore: 100;
  deductions: AdsAuditDeduction[];
  skipped: Array<{ rule: string; reason: string }>;
  categoryTotals: Record<AdsAuditDeductionCategory, { deducted: number; cap: number }>;
  finalScore: number;
  /**
   * Per-platform sub-scores when more than one ad platform is connected.
   * Absent on legacy rows and on single-platform audits.
   */
  platforms?: {
    google?: AdsAuditPlatformBreakdown;
    meta?: AdsAuditPlatformBreakdown;
  };
}

export interface AdsAudit {
  id: string;
  practiceId: string;
  locationId: string;
  score: number;
  summary: string;
  breakdown: AdsAuditBreakdown | null;
  runAt: string;
}

export interface TrendPoint {
  date: string;
  spend: number;
  conversions: number;
}

/** Page-level range for the Ads Overview (trend chart + summary metrics). */
export type OverviewRange = '7d' | '30d' | '90d' | 'ytd' | '1y';

export type CampaignRecType =
  | 'budget_increase'
  | 'budget_decrease'
  | 'pause'
  | 'new_headlines'
  | 'add_keywords'
  | 'add_negatives'
  | 'adjust_schedule'
  | 'adjust_geo'
  | 'new_creative';

export interface CampaignRec {
  id: string;
  title: string;
  description: string;
  category?: CampaignRecType | string | null;
  impact?: string | null;
  priority?: 'high' | 'medium' | 'low';
  actionData?: {
    type?: CampaignRecType;
    details?: Record<string, unknown>;
    impact_estimate?: string | null;
    urgency?: string;
  } | null;
  actionLabel?: string;
  editLabel?: string;
  status: 'pending' | 'approved' | 'dismissed';
}

export interface WeeklySummary {
  narrative: string;
  totalSpend: number;
  totalConversions: number;
  cpa: number;
  topCampaign: string | null;
}

/**
 * Preset ranges for the Performance page. `custom` uses `from`/`to` (YYYY-MM-DD).
 */
export type PerformanceRange = '15d' | '30d' | '90d' | 'ytd' | '1y' | 'custom';

export interface PerformanceRangeParams {
  range?: PerformanceRange;
  from?: string;
  to?: string;
}

/** Build the query params for a performance request, dropping empty fields. */
function perfParams(locationId: string, range?: PerformanceRangeParams) {
  return {
    locationId,
    ...(range?.range ? { range: range.range } : {}),
    ...(range?.range === 'custom' && range.from ? { from: range.from } : {}),
    ...(range?.range === 'custom' && range.to ? { to: range.to } : {}),
  };
}

// Agency path uses practiceId param; practice-user path uses /campaigns directly
const base = (practiceId?: string) =>
  practiceId ? `/agency/practices/${practiceId}/campaigns` : '/campaigns';

export const campaignsApi = {
  list: (locationId?: string, practiceId?: string) =>
    apiClient
      .get<ApiResponse<MarketingCampaign[]>>(base(practiceId), {
        params: locationId ? { locationId } : undefined,
      })
      .then((r) => r.data),

  get: (campaignId: string, practiceId?: string) =>
    apiClient
      .get<ApiResponse<MarketingCampaign>>(`${base(practiceId)}/${campaignId}`)
      .then((r) => r.data),

  create: (
    payload: { name: string; locationId: string; objective: string; brief: CampaignBrief },
    practiceId?: string,
  ) =>
    apiClient.post<ApiResponse<MarketingCampaign>>(base(practiceId), payload).then((r) => r.data),

  update: (
    campaignId: string,
    payload: { name?: string; objective?: string; brief?: Partial<CampaignBrief> },
    practiceId?: string,
  ) =>
    apiClient
      .patch<ApiResponse<MarketingCampaign>>(`${base(practiceId)}/${campaignId}`, payload)
      .then((r) => r.data),

  generate: (campaignId: string, practiceId?: string) =>
    apiClient
      .post<ApiResponse<MarketingCampaign>>(`${base(practiceId)}/${campaignId}/generate`)
      .then((r) => r.data),

  updateDeployment: (
    campaignId: string,
    deploymentId: string,
    platformData: PlatformData,
    practiceId?: string,
  ) =>
    apiClient
      .patch<
        ApiResponse<Deployment>
      >(`${base(practiceId)}/${campaignId}/deployments/${deploymentId}`, { platformData })
      .then((r) => r.data),

  listDeploymentAssets: (campaignId: string, deploymentId: string, practiceId?: string) =>
    apiClient
      .get<
        ApiResponse<CampaignAssetAttachment[]>
      >(`${base(practiceId)}/${campaignId}/deployments/${deploymentId}/assets`)
      .then((r) => r.data),

  attachAsset: (
    campaignId: string,
    deploymentId: string,
    payload: { mediaAssetId: string; role: string; order?: number },
    practiceId?: string,
  ) =>
    apiClient
      .post<
        ApiResponse<CampaignAssetAttachment>
      >(`${base(practiceId)}/${campaignId}/deployments/${deploymentId}/assets`, payload)
      .then((r) => r.data),

  detachAsset: (
    campaignId: string,
    deploymentId: string,
    mediaAssetId: string,
    role: string,
    practiceId?: string,
  ) =>
    apiClient
      .delete<
        ApiResponse<null>
      >(`${base(practiceId)}/${campaignId}/deployments/${deploymentId}/assets/${mediaAssetId}/${encodeURIComponent(role)}`)
      .then((r) => r.data),

  deleteDeployment: (
    campaignId: string,
    deploymentId: string,
    locationId: string,
    practiceId?: string,
  ) =>
    apiClient
      .delete<
        ApiResponse<null>
      >(`${base(practiceId)}/${campaignId}/deployments/${deploymentId}`, { params: { locationId } })
      .then((r) => r.data),

  /** Delete a single ad from a Meta deployment. Asset roles shift down. */
  deleteMetaAd: (campaignId: string, deploymentId: string, adIndex: number, practiceId?: string) =>
    apiClient
      .delete<
        ApiResponse<MarketingCampaign>
      >(`${base(practiceId)}/${campaignId}/deployments/${deploymentId}/ads/${adIndex}`)
      .then((r) => r.data),

  approve: (campaignId: string, deploymentId: string, practiceId?: string) =>
    apiClient
      .post<
        ApiResponse<MarketingCampaign>
      >(`${base(practiceId)}/${campaignId}/deployments/${deploymentId}/approve`)
      .then((r) => r.data),

  launch: (
    campaignId: string,
    deploymentId: string,
    practiceId?: string,
    launchStatus?: 'ENABLED' | 'PAUSED',
  ) =>
    apiClient
      .post<ApiResponse<MarketingCampaign>>(
        `${base(practiceId)}/${campaignId}/deployments/${deploymentId}/launch`,
        launchStatus ? { launchStatus } : undefined,
      )
      .then((r) => r.data),

  /** Pause or resume a launched deployment on the ad platform. Resuming resumes
   *  spend, so `confirmResume` must be true to turn a paused campaign back on. */
  setServingStatus: (
    campaignId: string,
    deploymentId: string,
    status: 'active' | 'paused',
    practiceId?: string,
    confirmResume?: boolean,
  ) =>
    apiClient
      .post<ApiResponse<MarketingCampaign>>(
        `${base(practiceId)}/${campaignId}/deployments/${deploymentId}/serving-status`,
        { status, ...(confirmResume ? { confirmResume: true } : {}) },
      )
      .then((r) => r.data),

  /** Discard a deployment's staged pending revision without pushing it. */
  discardPendingUpdate: (campaignId: string, deploymentId: string, practiceId?: string) =>
    apiClient
      .delete<
        ApiResponse<{ discarded: boolean }>
      >(`${base(practiceId)}/${campaignId}/deployments/${deploymentId}/pending-update`)
      .then((r) => r.data),

  /** Push a staged pending revision to the platform (Google Search only). */
  applyPendingUpdate: (
    campaignId: string,
    deploymentId: string,
    opts: ApplyPendingOptions,
    practiceId?: string,
  ) =>
    apiClient
      .post<
        ApiResponse<ApplyPendingResult>
      >(`${base(practiceId)}/${campaignId}/deployments/${deploymentId}/pending-update/apply`, opts)
      .then((r) => r.data),

  recommend: (practiceId?: string) =>
    apiClient
      .post<ApiResponse<MarketingCampaign[]>>(base(practiceId) + '/recommend')
      .then((r) => r.data),

  /** Delete a campaign that exists only in PracticeNova (never launched). */
  deleteCampaign: (campaignId: string, practiceId?: string, confirmPhrase?: string) =>
    apiClient
      .delete<ApiResponse<{ deleted: boolean }>>(
        `${base(practiceId)}/${campaignId}`,
        confirmPhrase ? { data: { confirmPhrase } } : undefined,
      )
      .then((r) => r.data),

  dismiss: (campaignId: string, practiceId?: string) =>
    apiClient
      .post<ApiResponse<null>>(`${base(practiceId)}/${campaignId}/dismiss`)
      .then((r) => r.data),

  /* ─── Per-campaign recommendations ─────────────────────────────────────────── */

  getCampaignRecs: (campaignId: string) =>
    apiClient
      .get<ApiResponse<CampaignRec[]>>(`/campaigns/${campaignId}/recommendations`)
      .then((r) => r.data),

  generateCampaignRecs: (campaignId: string) =>
    apiClient
      .post<ApiResponse<CampaignRec[]>>(`/campaigns/${campaignId}/recommendations/generate`)
      .then((r) => r.data),

  approveCampaignRec: (campaignId: string, recId: string) =>
    apiClient
      .post<ApiResponse<null>>(`/campaigns/${campaignId}/recommendations/${recId}/approve`)
      .then((r) => r.data),

  dismissCampaignRec: (campaignId: string, recId: string) =>
    apiClient
      .post<ApiResponse<null>>(`/campaigns/${campaignId}/recommendations/${recId}/dismiss`)
      .then((r) => r.data),

  /* ─── Performance ─────────────────────────────────────────────────────────── */

  performance: {
    summary: (locationId: string, range?: PerformanceRangeParams) =>
      apiClient
        .get<
          ApiResponse<PerformanceSummary>
        >('/campaigns/performance/summary', { params: perfParams(locationId, range) })
        .then((r) => r.data),

    channels: (locationId: string, range?: PerformanceRangeParams) =>
      apiClient
        .get<
          ApiResponse<ChannelRow[]>
        >('/campaigns/performance/channels', { params: perfParams(locationId, range) })
        .then((r) => r.data),

    campaigns: (locationId: string, range?: PerformanceRangeParams) =>
      apiClient
        .get<
          ApiResponse<CampaignRow[]>
        >('/campaigns/performance/campaigns', { params: perfParams(locationId, range) })
        .then((r) => r.data),

    searchTerms: (locationId: string, range?: PerformanceRangeParams) =>
      apiClient
        .get<
          ApiResponse<SearchTermRow[]>
        >('/campaigns/performance/search-terms', { params: perfParams(locationId, range) })
        .then((r) => r.data),

    heatmap: (locationId: string, range?: PerformanceRangeParams) =>
      apiClient
        .get<
          ApiResponse<HeatmapData>
        >('/campaigns/performance/heatmap', { params: perfParams(locationId, range) })
        .then((r) => r.data),
  },

  /* ─── Audit + Overview (Ads v9) ─────────────────────────────────────────── */

  audit: {
    get: (locationId: string) =>
      apiClient
        .get<ApiResponse<AdsAudit | null>>('/campaigns/audit', { params: { locationId } })
        .then((r) => r.data),
    run: (locationId: string) =>
      apiClient
        .post<ApiResponse<AdsAudit>>('/campaigns/audit/run', { locationId })
        .then((r) => r.data),
  },

  overview: {
    trend: (locationId: string, range: OverviewRange = '30d') =>
      apiClient
        .get<
          ApiResponse<TrendPoint[]>
        >('/campaigns/overview/trend', { params: { locationId, range } })
        .then((r) => r.data),
    weeklySummary: (locationId: string) =>
      apiClient
        .get<
          ApiResponse<WeeklySummary>
        >('/campaigns/overview/weekly-summary', { params: { locationId } })
        .then((r) => r.data),
    campaignTrend: (campaignId: string, range: '7d' | '30d' | '90d' = '30d') =>
      apiClient
        .get<ApiResponse<TrendPoint[]>>(`/campaigns/${campaignId}/trend`, { params: { range } })
        .then((r) => r.data),
    campaignSearchTerms: (campaignId: string) =>
      apiClient
        .get<ApiResponse<SearchTermRow[]>>(`/campaigns/${campaignId}/search-terms`)
        .then((r) => r.data),
    campaignHeatmap: (campaignId: string) =>
      apiClient
        .get<ApiResponse<HeatmapData>>(`/campaigns/${campaignId}/heatmap`)
        .then((r) => r.data),
    campaignInsights: (campaignId: string) =>
      apiClient
        .get<ApiResponse<CampaignInsightsData | null>>(`/campaigns/${campaignId}/insights`)
        .then((r) => r.data),
    locationCampaignRecs: (locationId: string) =>
      apiClient
        .get<
          ApiResponse<(CampaignRec & { marketingCampaignId: string })[]>
        >('/campaigns/overview/campaign-recommendations', { params: { locationId } })
        .then((r) => r.data),
  },

  /* ─── Autopilot (Ads v9) ───────────────────────────────────────────────── */

  autopilot: {
    getConfig: (locationId: string) =>
      apiClient
        .get<
          ApiResponse<LocationAdsConfig>
        >('/campaigns/autopilot/config', { params: { locationId } })
        .then((r) => r.data),
    updateConfig: (input: {
      locationId: string;
      monthlyAdBudget?: number;
      campaignApprovalPreference?: 'autopilot' | 'guided';
    }) =>
      apiClient
        .patch<ApiResponse<LocationAdsConfig>>('/campaigns/autopilot/config', input)
        .then((r) => r.data),
    approve: (plan: AutopilotPlan) =>
      apiClient
        .post<ApiResponse<AutopilotPlanResult>>('/campaigns/autopilot/approve', plan)
        .then((r) => r.data),
    rebalance: (input: {
      locationId: string;
      campaignBudgets: Array<{ campaignId: string; monthlyBudget: number }>;
    }) =>
      apiClient
        .post<
          ApiResponse<{ updated: number; total: number }>
        >('/campaigns/autopilot/rebalance', input)
        .then((r) => r.data),
  },
};

/* ─── Autopilot types ─────────────────────────────────────────────────────── */

export interface LocationAdsConfig {
  id: string;
  name: string;
  monthlyAdBudget: number | null;
  // NULL until the user explicitly picks via ModeSelectionScreen.
  campaignApprovalPreference: 'autopilot' | 'guided' | null;
}

export interface AutopilotCampaign {
  /** Existing MarketingCampaign id (from recommended row) — updated in place. */
  id: string;
  name: string;
  description?: string;
  objective: 'leads' | 'conversions' | 'awareness';
  monthlyBudget: number;
  channelMix: {
    google_ppc?: number;
    google_pmax?: number;
    google_lsa?: number;
    meta?: number;
  };
  aiRationale?: string;
  offerHook?: string;
  cta?: string;
  targetAudience?: string;
  keyMessages?: string[];
  geo?: string;
}

export interface AutopilotPlan {
  locationId: string;
  monthlyAdBudget: number;
  managementMode?: 'autopilot' | 'guided';
  keepExistingCampaignIds: string[];
  campaigns: AutopilotCampaign[];
}

export interface AutopilotPlanResult {
  locationId: string;
  monthlyAdBudget: number;
  activatedCampaignIds: string[];
}

/* ─── Activity (Ads v9) ───────────────────────────────────────────────────── */

export type ActivityModule = 'ads' | 'seo' | 'strategy' | 'gbp';

export interface ActivityRecord {
  id: string;
  practiceId: string;
  locationId: string | null;
  module: ActivityModule;
  category: string;
  message: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export type RecapRange = 'last7' | 'last30' | 'last90' | 'ytd' | 'lifetime';

export interface ActivityRecap {
  range: RecapRange;
  spend: number;
  spendDeltaPct: number | null;
  conversions: number;
  conversionsDeltaPct: number | null;
  cpa: number;
  cpaDeltaPct: number | null;
  narrative: string;
}

export const activityApi = {
  list: (params: { module?: ActivityModule; locationId?: string; limit?: number }) =>
    apiClient.get<ApiResponse<ActivityRecord[]>>('/activity', { params }).then((r) => r.data),
  recap: (locationId: string, range: RecapRange = 'last7') =>
    apiClient
      .get<ApiResponse<ActivityRecap>>('/activity/recap', { params: { locationId, range } })
      .then((r) => r.data),
};
